import { useEffect, useMemo, useState } from "react";
import NoData from "@/components/NoData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { useLiveData } from "@/hooks/useLiveData";
import { FINISHED_STATUSES } from "@/contants";
import { getRoundLabel } from "@/utils/leagueFixturesByLeague";
import SidebarMatch from "./SidebarMatch";
import RoundSelect from "./RoundSelect";

const MAX_ALL_ROUNDS = 3;
const EMPTY_ENTRIES = [];

function matchIsFinished(match) {
  const status = Number(match?.status);
  return Number.isFinite(status) && FINISHED_STATUSES.includes(status);
}

function roundIsUpcoming(matches) {
  return (matches || []).some((m) => !matchIsFinished(m));
}

function nearestRoundKey(entries) {
  if (!entries?.length) return null;
  for (const [round, matches] of entries) {
    if (roundIsUpcoming(matches)) return String(round);
  }
  return String(entries[entries.length - 1][0]);
}

function capUpcomingRounds(entries, max = MAX_ALL_ROUNDS) {
  const upcoming = (entries || []).filter(([, matches]) => roundIsUpcoming(matches));
  const source = upcoming.length ? upcoming : entries || [];
  return source.slice(0, max);
}

/**
 * @param {object} props
 * @param {object} props.league
 * @param {Array<[string|number, object[]]>} props.initialMatchesEntries
 * @param {object} [props.themeStyle]
 * @param {boolean} [props.showRoundSelect]
 * @param {(key: string|number, matches: object[]) => string} [props.getGroupLabel]
 */
export default function MatchList({
  league,
  initialMatchesEntries,
  themeStyle,
  showRoundSelect = true,
  getGroupLabel = getRoundLabel,
}) {
  const { clearAllData } = useLiveData();
  const roundEntries = initialMatchesEntries || EMPTY_ENTRIES;
  const [selectedRound, setSelectedRound] = useState(() =>
    showRoundSelect ? nearestRoundKey(roundEntries) : null,
  );

  const matchIds = useMemo(() => {
    const ids = [];
    for (const [, matches] of roundEntries) {
      for (const m of matches ?? []) {
        const n = Number(m.id);
        if (Number.isFinite(n)) ids.push(n);
      }
    }
    return ids;
  }, [roundEntries]);

  useLiveUpdates(matchIds);

  useEffect(
    () => () => {
      clearAllData();
    },
    [clearAllData],
  );

  useEffect(() => {
    if (!showRoundSelect) {
      setSelectedRound(null);
      return;
    }
    setSelectedRound(nearestRoundKey(roundEntries));
  }, [league?.id, roundEntries, showRoundSelect]);

  const visibleEntries = useMemo(() => {
    if (!showRoundSelect) return roundEntries;
    if (selectedRound == null) return capUpcomingRounds(roundEntries);
    return roundEntries.filter(([round]) => String(round) === String(selectedRound));
  }, [roundEntries, selectedRound, showRoundSelect]);

  const totalMatches = useMemo(() => {
    let n = 0;
    for (const [, matches] of visibleEntries) n += matches?.length ?? 0;
    return n;
  }, [visibleEntries]);

  if (roundEntries.length === 0) {
    return <NoData message="Không có trận đấu" />;
  }

  const showGroupHeads = !showRoundSelect
    ? visibleEntries.length > 0
    : selectedRound == null && visibleEntries.length > 1;

  return (
    <div className="kbv-ltd-sb-list">
      {showRoundSelect && roundEntries.length > 1 ? (
        <RoundSelect
          roundEntries={roundEntries}
          selectedRound={selectedRound}
          onSelect={setSelectedRound}
          themeStyle={themeStyle}
        />
      ) : null}

      {totalMatches === 0 ? (
        <NoData message="Không có trận đấu" />
      ) : (
        <div className="kbv-ltd-sb-card">
          {visibleEntries.map(([round, matches]) => (
            <div key={`${league?.id ?? ""}-${String(round)}`} className="kbv-ltd-sb-group">
              {showGroupHeads ? (
                <div className="kbv-ltd-sb-round-head">
                  <h3>{getGroupLabel(round, matches)}</h3>
                </div>
              ) : null}
              <div className="kbv-ltd-sb-matches">
                {(matches || []).map((match, index) => (
                  <SidebarMatch
                    key={match.id}
                    match={match}
                    isLastItem={index === matches.length - 1}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
